import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { trackAnalytics } from '../api';

const SESSION_KEY = 'eat_session_id';

// ── Lightweight client-side device/browser detection ───────────────
function detectDevice(ua) {
  if (/iPad|Tablet|PlayBook|Silk/i.test(ua) || (/Android/i.test(ua) && !/Mobile/i.test(ua)))
    return 'Tablet';
  if (/Mobi|iPhone|iPod|Android.*Mobile|Windows Phone/i.test(ua)) return 'Mobile';
  return 'Desktop';
}

function detectOs(ua) {
  if (/Windows NT/i.test(ua)) return 'Windows';
  if (/Mac OS X/i.test(ua)) return 'macOS';
  if (/Android/i.test(ua)) return 'Android';
  if (/iPhone|iPad|iPod/i.test(ua)) return 'iOS';
  if (/Linux/i.test(ua)) return 'Linux';
  return 'Other';
}

function detectBrowser(ua) {
  if (/Edg\//i.test(ua)) return 'Edge';
  if (/OPR\/|Opera/i.test(ua)) return 'Opera';
  if (/Chrome\//i.test(ua) && !/Edg\//i.test(ua)) return 'Chrome';
  if (/Firefox\//i.test(ua)) return 'Firefox';
  if (/Safari\//i.test(ua) && !/Chrome\//i.test(ua)) return 'Safari';
  return 'Other';
}

// RFC 4122 v4 UUID, format-valid so the backend's Guid-typed SessionId never
// rejects it - used when crypto.randomUUID isn't available (older browsers,
// some in-app/WebView browsers). Not cryptographically secure, but this is
// just a session correlation id, not a security token.
function uuidV4Fallback() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function getSessionId() {
  let id = sessionStorage.getItem(SESSION_KEY);
  if (!id) {
    id =
      typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : uuidV4Fallback();
    sessionStorage.setItem(SESSION_KEY, id);
  }
  return id;
}

/**
 * Tracks visits and page views. Opens a session once per browser session,
 * then records a page view (with time-on-page) each time the visitor leaves
 * a page — either by navigating within the SPA or by closing/hiding the tab.
 */
export default function useAnalytics() {
  const { pathname } = useLocation();
  const sessionId = useRef(null);
  const sessionSent = useRef(false);
  const currentPath = useRef(pathname);
  const enteredAt = useRef(Date.now());

  // Open the session once, sending device/environment details.
  useEffect(() => {
    sessionId.current = getSessionId();
    if (sessionStorage.getItem(SESSION_KEY + '_sent')) {
      sessionSent.current = true;
    }
    if (!sessionSent.current) {
      const ua = navigator.userAgent || '';
      // Session-open ping: no path — page views are recorded on leave below,
      // so the entry page is counted exactly once when the visitor leaves it.
      trackAnalytics({
        sessionId: sessionId.current,
        deviceType: detectDevice(ua),
        os: detectOs(ua),
        browser: detectBrowser(ua),
        language: navigator.language,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        referrer: document.referrer || 'Direct',
        screenSize: `${window.screen.width}x${window.screen.height}`,
      });
      sessionSent.current = true;
      sessionStorage.setItem(SESSION_KEY + '_sent', '1');
      currentPath.current = window.location.pathname;
      enteredAt.current = Date.now();
    }
  }, []);

  // Helper: record the page we're leaving, with time spent on it.
  const flushed = useRef(false);
  const flush = () => {
    if (!sessionId.current) return;
    const duration = Date.now() - enteredAt.current;
    trackAnalytics({
      sessionId: sessionId.current,
      path: currentPath.current,
      durationMs: Math.min(duration, 86_400_000),
    });
  };

  // On SPA route change: flush the previous page, then start timing the new one.
  useEffect(() => {
    if (pathname === currentPath.current) return;
    flush();
    currentPath.current = pathname;
    enteredAt.current = Date.now();
    flushed.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  // On tab hide / unload: flush the current page once (final duration).
  //
  // pagehide and visibilitychange are NOT interchangeable here: pagehide
  // fires whenever the document is going away (navigation, reload, close),
  // regardless of tab visibility - a same-tab refresh usually never
  // transitions document.visibilityState to "hidden" at all, since the tab
  // never loses focus. Gating pagehide behind a visibilityState check (as
  // this used to do) meant refreshes silently dropped their page view.
  useEffect(() => {
    const onPageHide = () => {
      if (!flushed.current) {
        flush();
        flushed.current = true;
      }
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden' && !flushed.current) {
        flush();
        flushed.current = true;
      }
    };
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('visibilitychange', onVisibilityChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
